import type { PrivySolanaClientType } from "./glyph-account";
import type { LinkedAccountLike } from "./privy-account";

/** Account-list presentation only: never a connection, signer or asset recipient. */
export function externalSolanaAddresses(
  accounts: readonly LinkedAccountLike[]
): string[] {
  return [
    ...new Set(
      accounts.flatMap((account) =>
        account.type === "wallet" &&
        account.chainType === "solana" &&
        account.walletClientType !== "privy" &&
        account.connectorType !== "embedded" &&
        typeof account.address === "string" &&
        account.address.length > 0
          ? [account.address]
          : []
      )
    ),
  ].sort();
}

interface LinkInput {
  userId: string;
  address: string;
  clientType?: PrivySolanaClientType;
  current: () => boolean;
  signMessage: (bytes: Uint8Array) => Promise<Uint8Array>;
  generateSiwsMessage: (input: { address: string }) => Promise<string>;
  linkWithSiws: (input: {
    message: string;
    signature: string;
    walletClientType?: PrivySolanaClientType;
    connectorType: "solana_adapter";
  }) => Promise<{
    user: { id: string; linkedAccounts: readonly LinkedAccountLike[] };
  }>;
}

/** A successful HTTP/SDK call alone does not confirm the intended account link. */
export async function linkGlyphSolanaWallet(input: LinkInput): Promise<void> {
  const current = () => {
    if (!input.current())
      throw new Error("The selected Glyph account or wallet changed.");
  };
  current();
  const message = await input.generateSiwsMessage({ address: input.address });
  current();
  const signature = await input.signMessage(new TextEncoder().encode(message));
  current();
  const result = await input
    .linkWithSiws({
      message,
      signature: base64(signature),
      ...(input.clientType ? { walletClientType: input.clientType } : {}),
      connectorType: "solana_adapter",
    })
    .catch(rethrowLinkError);
  current();
  if (
    result.user.id !== input.userId ||
    !externalSolanaAddresses(result.user.linkedAccounts).includes(input.address)
  )
    throw new Error(
      "Glyph did not confirm this wallet on your account. Retry linking."
    );
}

function base64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function rethrowLinkError(error: unknown): never {
  const code =
    error && typeof error === "object"
      ? Reflect.get(error, "privyErrorCode")
      : null;
  if (code === "linked_to_another_user" || code === "account_transfer_required")
    throw new Error(
      "This wallet belongs to another Glyph account. Sign in to that account or use Glyph account recovery before linking it here. Your game history stays with this wallet."
    );
  throw error;
}
